import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MousePointer2, Plus, Trash2, Undo2, Upload, Sprout } from 'lucide-react';
import { fromBlob } from 'geotiff';
import { formatNumber } from '../../utils/erpLocale';
import { invocarMotorPythonBloquesPoligonoArchivo, type MotorPythonBatchResponse } from './deteccionService';

type PointNorm = [number, number];
type BlockDraw = { bloque_num: number; puntos: PointNorm[] };

function clamp01(v: number) {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}

function pointsToSvg(points: PointNorm[]) {
  return points.map((p) => `${p[0] * 100},${p[1] * 100}`).join(' ');
}

function centroid(points: PointNorm[]) {
  if (points.length === 0) return [0, 0] as const;
  const sx = points.reduce((a, p) => a + p[0], 0);
  const sy = points.reduce((a, p) => a + p[1], 0);
  return [sx / points.length, sy / points.length] as const;
}

function extractCaptureId(fileName: string): string {
  const name = String(fileName || '');
  const m = name.match(/_(\d{4})_(?:D|MS_[A-Z]+)\./i);
  if (m?.[1]) return m[1];
  const fallback = name.match(/_(\d{4})\D*$/);
  return fallback?.[1] || '';
}

function normalizeDebugUrl(raw?: string): string | undefined {
  if (!raw) return undefined;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.pathname.startsWith('/resultados/')) {
      return `${window.location.origin}/api/python${u.pathname}${u.search || ''}`;
    }
    return u.toString();
  } catch {
    if (raw.startsWith('/resultados/')) return `${window.location.origin}/api/python${raw}`;
    return raw;
  }
}

export function ConteoPlantasV5SimplePage() {
  const [flightFiles, setFlightFiles] = useState<File[]>([]);
  const [selectedViewName, setSelectedViewName] = useState('');
  const [viewFile, setViewFile] = useState<File | null>(null);
  const [detectFile, setDetectFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [points, setPoints] = useState<PointNorm[]>([]);
  const [blocks, setBlocks] = useState<BlockDraw[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<MotorPythonBatchResponse | null>(null);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);
  const movedDuringPanRef = useRef(false);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const viewCaptureId = useMemo(() => (viewFile ? extractCaptureId(viewFile.name) : ''), [viewFile]);
  const detectCaptureId = useMemo(() => (detectFile ? extractCaptureId(detectFile.name) : ''), [detectFile]);
  const filesMismatch = useMemo(() => {
    if (!viewFile || !detectFile) return false;
    if (!viewCaptureId || !detectCaptureId) return false;
    return viewCaptureId !== detectCaptureId;
  }, [viewFile, detectFile, viewCaptureId, detectCaptureId]);
  const viewCandidates = useMemo(
    () => flightFiles.filter((f) => /_D\.(jpg|jpeg)$/i.test(f.name)),
    [flightFiles]
  );

  function findMatchingNir(view: File, files: File[]): File | null {
    const id = extractCaptureId(view.name);
    if (!id) return null;
    const byIdAndSuffix = files.find((f) => {
      if (!/\.(tif|tiff)$/i.test(f.name)) return false;
      if (!/_MS_NIR\./i.test(f.name)) return false;
      return extractCaptureId(f.name) === id;
    });
    return byIdAndSuffix || null;
  }

  function applyViewWithAutoDetect(nextView: File) {
    setViewFile(nextView);
    const matchedNir = findMatchingNir(nextView, flightFiles);
    if (matchedNir) {
      setDetectFile(matchedNir);
      setError('');
      return;
    }
    setError(`No se encontró archivo MS_NIR para ${nextView.name}.`);
  }

  useEffect(() => {
    let revokedUrl = '';
    let active = true;
    async function buildPreview() {
      const sourceFile = viewFile || detectFile;
      if (!sourceFile) {
        setPreviewUrl('');
        setPoints([]);
        setBlocks([]);
        setResult(null);
        return;
      }
      setPoints([]);
      setBlocks([]);
      setResult(null);

      try {
        const isTiff = /\.(tif|tiff)$/i.test(sourceFile.name) || /tiff/i.test(sourceFile.type || '');
        if (!isTiff) {
          const nextUrl = URL.createObjectURL(sourceFile);
          revokedUrl = nextUrl;
          if (active) setPreviewUrl(nextUrl);
          return;
        }

        const tiff = await fromBlob(sourceFile);
        const image = await tiff.getImage();
        const width = image.getWidth();
        const height = image.getHeight();
        const samples = image.getSamplesPerPixel();
        const rasters = (await image.readRasters({ interleave: true })) as unknown as ArrayLike<number>;
        const pixels = width * height;
        if (!rasters || rasters.length < pixels) throw new Error('Raster vacío');

        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < rasters.length; i++) {
          const v = Number(rasters[i]);
          if (!Number.isFinite(v)) continue;
          if (v < min) min = v;
          if (v > max) max = v;
        }
        if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
          min = 0;
          max = 1;
        }
        const range = Math.max(1e-6, max - min);
        const to8 = (v: number) => Math.max(0, Math.min(255, Math.round(((v - min) / range) * 255)));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No se pudo crear canvas');
        const imgData = ctx.createImageData(width, height);
        for (let i = 0; i < pixels; i++) {
          const dst = i * 4;
          if (samples >= 3) {
            const src = i * samples;
            imgData.data[dst] = to8(Number(rasters[src]));
            imgData.data[dst + 1] = to8(Number(rasters[src + 1]));
            imgData.data[dst + 2] = to8(Number(rasters[src + 2]));
          } else {
            const v = to8(Number(rasters[i]));
            imgData.data[dst] = v;
            imgData.data[dst + 1] = v;
            imgData.data[dst + 2] = v;
          }
          imgData.data[dst + 3] = 255;
        }
        ctx.putImageData(imgData, 0, 0);
        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('No se pudo generar preview'))), 'image/png', 0.92);
        });
        const previewBlobUrl = URL.createObjectURL(blob);
        revokedUrl = previewBlobUrl;
        if (active) setPreviewUrl(previewBlobUrl);
      } catch {
        const fallbackUrl = URL.createObjectURL(sourceFile);
        revokedUrl = fallbackUrl;
        if (active) setPreviewUrl(fallbackUrl);
      }
    }
    buildPreview();
    return () => {
      active = false;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, [viewFile, detectFile]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = (target?.tagName || '').toLowerCase();
      const isEditable = !!target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
      if (isEditable) return;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        setPoints((prev) => prev.slice(0, -1));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        setPoints((prev) => prev.slice(0, -1));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const nextBlockNum = useMemo(() => {
    if (blocks.length === 0) return 1;
    return Math.max(...blocks.map((b) => b.bloque_num)) + 1;
  }, [blocks]);

  function onImageClick(e: React.MouseEvent<HTMLImageElement>) {
    if (movedDuringPanRef.current) return;
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const nx = clamp01((e.clientX - rect.left) / rect.width);
    const ny = clamp01((e.clientY - rect.top) / rect.height);
    setPoints((prev) => [...prev, [nx, ny]]);
  }

  function zoomIn() {
    setZoom((z) => Math.min(6, Number((z + 0.2).toFixed(2))));
  }

  function zoomOut() {
    setZoom((z) => Math.max(0.4, Number((z - 0.2).toFixed(2))));
  }

  function resetZoom() {
    setZoom(1);
    if (previewContainerRef.current) {
      previewContainerRef.current.scrollLeft = 0;
      previewContainerRef.current.scrollTop = 0;
    }
  }

  function onPanStart(e: React.MouseEvent<HTMLDivElement>) {
    if (zoom <= 1) return;
    e.preventDefault();
    if (!previewContainerRef.current) return;
    movedDuringPanRef.current = false;
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: previewContainerRef.current.scrollLeft,
      scrollTop: previewContainerRef.current.scrollTop,
    };
    setIsPanning(true);
  }

  function onPanMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!isPanning || !panStartRef.current || !previewContainerRef.current) return;
    e.preventDefault();
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) movedDuringPanRef.current = true;
    previewContainerRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
    previewContainerRef.current.scrollTop = panStartRef.current.scrollTop - dy;
  }

  function onPanEnd() {
    panStartRef.current = null;
    setIsPanning(false);
    window.setTimeout(() => {
      movedDuringPanRef.current = false;
    }, 0);
  }

  function addBlock() {
    if (points.length < 3) {
      setError('Marca al menos 3 puntos para cerrar un bloque.');
      return;
    }
    setBlocks((prev) => [...prev, { bloque_num: nextBlockNum, puntos: [...points] }]);
    setPoints([]);
    setError('');
  }

  async function runDetect() {
    if (!detectFile) {
      setError('Sube el archivo de detección (MS_NIR.TIF).');
      return;
    }
    if (blocks.length === 0) {
      setError('Marca al menos un bloque.');
      return;
    }
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const payload = await invocarMotorPythonBloquesPoligonoArchivo(
        { minArea: 20, maxArea: 800 },
        blocks.map((b) => ({ bloque_num: b.bloque_num, puntos: b.puntos })),
        detectFile,
        null,
        'upload',
        { areaMode: 'auto', gsdM: 0.05, detectMode: 'conteo5_simple' }
      );
      setResult(payload);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="w-full h-full flex flex-col bg-surface">
      {/* HEADER TOP BAR */}
      <div className="bg-surface-raised border-b border-line px-6 py-3 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">📊 Conteo de Plantas V5</h1>
        <div className="flex gap-4 items-center">
          <label className="flex cursor-pointer items-center gap-2 rounded border border-line bg-surface px-4 py-2 text-sm text-ink hover:border-emerald-600/50">
            <Upload size={18} />
            {viewFile ? viewFile.name.slice(0, 25) : 'Cargar Imagen'}
            <input
              type="file"
              accept=".tif,.tiff,image/tiff,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setViewFile(f);
                  if (flightFiles.length > 0) {
                    const nir = findMatchingNir(f, flightFiles);
                    if (nir) setDetectFile(nir);
                  }
                }
              }}
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded border border-line bg-surface px-4 py-2 text-sm text-ink hover:border-emerald-600/50">
            <Upload size={18} />
            {detectFile ? detectFile.name.slice(0, 25) : 'MS_NIR'}
            <input
              type="file"
              accept=".tif,.tiff,image/tiff"
              className="hidden"
              onChange={(e) => setDetectFile(e.target.files?.[0] || null)}
            />
          </label>
          {viewFile && detectFile && !filesMismatch && (
            <div className="text-sm text-emerald-300 bg-emerald-950/20 rounded px-3 py-1">
              ✅ Sincronizados
            </div>
          )}
          {filesMismatch && (
            <div className="text-sm text-amber-300 bg-amber-950/20 rounded px-3 py-1">
              ⚠️ No coinciden
            </div>
          )}
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="bg-surface-raised border-b border-line px-6 py-3 flex gap-3">
        <button
          onClick={() => setPoints((p) => p.slice(0, -1))}
          disabled={points.length === 0}
          className="px-4 py-2 rounded border border-line text-sm disabled:opacity-50 hover:bg-white/5"
          title="Deshacer último punto"
        >
          ↶ Deshacer
        </button>
        <button
          onClick={() => setPoints([])}
          disabled={points.length === 0}
          className="px-4 py-2 rounded border border-line text-sm disabled:opacity-50 hover:bg-white/5"
          title="Limpiar puntos"
        >
          🗑️ Limpiar
        </button>
        <button
          onClick={addBlock}
          disabled={points.length < 3}
          className="px-4 py-2 rounded border border-emerald-600/50 bg-emerald-950/30 text-emerald-300 text-sm disabled:opacity-50 hover:bg-emerald-950/50"
          title="Agregar bloque"
        >
          + Bloque {nextBlockNum}
        </button>
        <button
          onClick={runDetect}
          disabled={!detectFile || blocks.length === 0 || running}
          className="px-6 py-2 rounded bg-emerald-600 text-white font-semibold text-sm disabled:opacity-50 hover:bg-emerald-700"
          title="Ejecutar conteo"
        >
          🌱 {running ? 'Procesando...' : 'Contar'}
        </button>
        {error && <div className="text-sm text-red-300 bg-red-950/20 rounded px-4 py-2 ml-auto">{error}</div>}
      </div>

      {/* CONTENEDOR PRINCIPAL - IMAGEN A FULL */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* ÁREA DE IMAGEN - OCUPA TODO EL ESPACIO */}
        {previewUrl ? (
          <div className="flex-1 bg-black/40 overflow-hidden flex flex-col min-h-0">
            <div className="flex items-center justify-between bg-surface-raised px-6 py-3 border-b border-line">
              <span className="text-lg font-semibold ink-2">
                📍 Puntos: {points.length}  •  📦 Bloques: {blocks.length}
              </span>
              <div className="flex items-center gap-3">
                <button onClick={zoomOut} className="px-3 py-2 text-base rounded hover:bg-white/10">−</button>
                <span className="w-20 text-center text-base font-semibold">{Math.round(zoom * 100)}%</span>
                <button onClick={zoomIn} className="px-3 py-2 text-base rounded hover:bg-white/10">+</button>
                <button onClick={resetZoom} className="px-3 py-2 text-base rounded hover:bg-white/10">↺</button>
              </div>
            </div>
          <div
            ref={previewContainerRef}
            className="flex-1 overflow-auto"
            onMouseMove={onPanMove}
            onMouseUp={onPanEnd}
            onMouseLeave={onPanEnd}
          >
            <div className="flex items-start justify-start">
              <div
                className={`relative inline-block origin-top-left ${zoom > 1 ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
                style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
                onMouseDown={onPanStart}
                onWheel={(e) => {
                  e.preventDefault();
                  if (e.deltaY < 0) zoomIn();
                  else zoomOut();
                }}
              >
                <img
                  ref={imgRef}
                  src={previewUrl}
                  alt="preview"
                  className="block h-auto w-auto select-none"
                  draggable={false}
                  onClick={onImageClick}
                />
                <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {blocks.map((b) => {
                    const c = centroid(b.puntos);
                    return (
                      <g key={`blk-${b.bloque_num}`}>
                        <polygon
                          points={pointsToSvg(b.puntos)}
                          fill="rgba(34,197,94,0.2)"
                          stroke="#22c55e"
                          strokeWidth="0.25"
                          vectorEffect="non-scaling-stroke"
                        />
                        <text x={c[0] * 100} y={c[1] * 100} fontSize="2" fill="#22c55e" textAnchor="middle">
                          B{b.bloque_num}
                        </text>
                      </g>
                    );
                  })}
                  {points.length >= 2 && (
                    <polyline
                      points={pointsToSvg(points)}
                      fill="rgba(59,130,246,0.1)"
                      stroke="#60a5fa"
                      strokeWidth="0.25"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  {points.map((p, idx) => (
                    <circle key={idx} cx={p[0] * 100} cy={p[1] * 100} r="0.4" fill="#60a5fa" vectorEffect="non-scaling-stroke" />
                  ))}
                </svg>
              </div>
            </div>
          </div>
          </div>
        ) : (
          <div className="flex-1 bg-surface/50 flex items-center justify-center">
            <div className="text-center">
              <Upload size={64} className="mx-auto ink-3 mb-4" />
              <p className="text-lg ink-2">Carga una imagen para comenzar</p>
            </div>
          </div>
        )}
      </div>

      {/* PANEL RESULTADOS - ABAJO COMPACTO */}
      {result && (
        <div className="border-t border-line bg-surface-raised px-6 py-3 max-h-40 overflow-y-auto">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs ink-2 border-b border-line sticky top-0 bg-surface-raised">
                <tr>
                  <th className="pb-2 pr-4">Bloque</th>
                  <th className="pb-2 pr-4">Área (ha)</th>
                  <th className="pb-2 pr-4">Plantas</th>
                  <th className="pb-2 pr-4">Plantas/Cama</th>
                  <th className="pb-2 pr-4">Hileras</th>
                  <th className="pb-2 pr-4">Densidad</th>
                  <th className="pb-2">KPI</th>
                </tr>
              </thead>
              <tbody>
                {(result.bloques || []).map((row, idx) => (
                  <tr key={idx} className="border-b border-line hover:bg-white/5">
                    <td className="py-2 pr-4 font-semibold">B{row.bloque_num}</td>
                    <td className="py-2 pr-4">{Number(row.area_ha || 0).toFixed(4)}</td>
                    <td className="py-2 pr-4 font-bold">{formatNumber(Number(row.conteo || 0))}</td>
                    <td className="py-2 pr-4">{Number(row.plantas_promedio_cama || 0).toFixed(1)}</td>
                    <td className="py-2 pr-4">{row.hileras || 0}</td>
                    <td className="py-2 pr-4">{formatNumber(Math.round(Number(row.densidad || 0)))}</td>
                    <td className="py-2 text-emerald-300 font-semibold">{Number(row.kpi || 0).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConteoPlantasV5SimplePage;
